/**
 * Service de gestion des préférences régionales
 */

import { supabase } from '@/lib/supabase';
import { LOCALE_DEFAULTS } from '@/i18n/locale-defaults';
import { i18n, normalizeLocale, onLocaleChange, type Locale } from '@/i18n';

export type WeekStartDay = 'monday' | 'sunday';
export type TimeFormat = '24h' | '12h';

export interface LocalePreferences {
    weekStartDay: WeekStartDay;
    timeFormat: TimeFormat;
    language: string;
    /** Optional user override for date format (e.g. "YYYY-MM-DD") instead of the locale default. */
    dateFormatOverride?: string;
}

export class LocaleService {
    private static instance: LocaleService;
    private preferences: LocalePreferences;
    private listeners: Array<() => void> = [];
    // Dédup du chargement DB : chaque composant montant useLocale() déclenchait
    // son propre fetch user_profiles (HARMONIA-3 : N+1 sur /tabs/tasks).
    private loadFromDbPromise: Promise<void> | null = null;
    private dbLoadedForUserId: string | null = null;
    // Dernière langue effectivement poussée en base sur cette session : évite de
    // réécrire user_profiles à chaque setLocale() (le LoginPage en appelle un à
    // chaque montage) sans rater la toute première écriture.
    private persistedLanguage: string | null = null;

    private constructor() {
        this.preferences = {
            weekStartDay: 'monday',
            timeFormat: '24h',
            language: 'fr',
        };
        this.loadFromStorage();
    }

    public static getInstance(): LocaleService {
        if (!LocaleService.instance) LocaleService.instance = new LocaleService();
        return LocaleService.instance;
    }

    private loadFromStorage(): void {
        try {
            const stored = localStorage.getItem('locale-preferences');
            if (!stored) return;
            const parsed = JSON.parse(stored) as LocalePreferences & { dateFormat?: string };
            // Migration: legacy `dateFormat` field → `dateFormatOverride`
            if (parsed.dateFormat && !parsed.dateFormatOverride) {
                parsed.dateFormatOverride = parsed.dateFormat;
            }
            delete parsed.dateFormat;
            this.preferences = { ...this.preferences, ...parsed };
        } catch (error) {
            console.warn('Erreur lors du chargement des préférences de localisation:', error);
        }
    }

    private saveToStorage(): void {
        try { localStorage.setItem('locale-preferences', JSON.stringify(this.preferences)); }
        catch (error) { console.warn('Erreur lors de la sauvegarde des préférences:', error); }
    }

    public async updatePreferences(newPreferences: Partial<LocalePreferences>): Promise<void> {
        this.preferences = { ...this.preferences, ...newPreferences };
        this.saveToStorage();
        await this.saveToDatabase();
        this.notifyListeners();
    }

    public addListener(listener: () => void): void { this.listeners.push(listener); }
    public removeListener(listener: () => void): void {
        const idx = this.listeners.indexOf(listener);
        if (idx > -1) this.listeners.splice(idx, 1);
    }
    private notifyListeners(): void { this.listeners.forEach(l => l()); }

    private async saveToDatabase(): Promise<void> {
        try {
            // getSession() lit la session locale (pas d'aller-retour réseau comme getUser())
            const { data: { session } } = await supabase.auth.getSession();
            const user = session?.user;
            if (!user) return;
            const { error } = await supabase
                .from('user_profiles')
                .update({
                    week_start_day: this.preferences.weekStartDay,
                    time_format: this.preferences.timeFormat,
                    language: this.preferences.language,
                })
                .eq('id', user.id);
            if (error) console.error('Erreur sauvegarde préférences DB:', error);
        } catch (error) { console.error('Erreur sauvegarde préférences DB:', error); }
    }

    public async loadFromDatabase(force = false): Promise<void> {
        if (this.loadFromDbPromise) return this.loadFromDbPromise;
        this.loadFromDbPromise = this.doLoadFromDatabase(force);
        try {
            await this.loadFromDbPromise;
        } finally {
            this.loadFromDbPromise = null;
        }
    }

    private async doLoadFromDatabase(force: boolean): Promise<void> {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const user = session?.user;
            if (!user) return;
            // Une fois par session et par utilisateur suffit : les préférences ne
            // changent que via updatePreferences() qui écrit déjà DB + localStorage.
            if (!force && this.dbLoadedForUserId === user.id) return;
            const { data, error } = await supabase
                .from('user_profiles')
                .select('week_start_day, time_format, language')
                .eq('id', user.id)
                .single() as {
                    data: { week_start_day: string | null; time_format: string | null; language: string | null } | null;
                    error: unknown;
                };
            if (error) { console.error('Erreur chargement préférences DB:', error); return; }
            this.dbLoadedForUserId = user.id;
            if (data) {
                if (data.week_start_day) this.preferences.weekStartDay = data.week_start_day as WeekStartDay;
                if (data.time_format) this.preferences.timeFormat = data.time_format as TimeFormat;
                // La langue ne se lit PAS depuis la base : c'est l'app qui dit la
                // vérité, la colonne la suit. On en profite pour réparer les profils
                // déjà divergents — impossible à faire côté serveur, qui ne sait pas
                // ce que le parent a sous les yeux (TW 17712811).
                this.reconcileProfileLanguage(user.id, data.language);
                this.saveToStorage();
                this.notifyListeners();
            }
        } catch (error) { console.error('Erreur chargement préférences DB:', error); }
    }

    /**
     * Aligne user_profiles.language sur la langue affichée. Appelé au chargement
     * des préférences, une fois la session connue.
     */
    private reconcileProfileLanguage(userId: string, storedLanguage: string | null): void {
        const visible = this.getLanguage();
        this.preferences.language = visible;
        const stored = storedLanguage ? normalizeLocale(storedLanguage.trim()) : null;
        if (stored === visible) {
            this.persistedLanguage = visible;
            return;
        }
        console.info('[locale] profil désaligné, réécriture:', stored, '→', visible);
        this.persistedLanguage = visible;
        void this.writeLanguageToProfile(userId, visible);
    }

    private async writeLanguageToProfile(userId: string, language: string): Promise<void> {
        const { error } = await supabase
            .from('user_profiles')
            .update({ language })
            .eq('id', userId);
        if (error) console.error('Erreur réconciliation langue profil:', error);
    }

    public getPreferences(): LocalePreferences { return { ...this.preferences }; }

    public getWeekStartIndex(): number {
        return this.preferences.weekStartDay === 'monday' ? 1 : 0;
    }

    public getTimeFormat(): TimeFormat { return this.preferences.timeFormat; }

    /**
     * La langue affichée, pas celle mémorisée dans les préférences : c'est
     * vue-i18n qui fait foi. Avant, un anglophone tout juste installé lisait le
     * défaut 'fr' du service et recevait ses tâches par défaut en français.
     */
    public getLanguage(): string { return normalizeLocale(i18n.global.locale.value as string); }

    /** Format a date using Intl.DateTimeFormat of the current language (or override if set). */
    public formatDate(date: Date): string {
        const locale = this.getLanguage() || 'en';
        return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
    }

    /**
     * Updates the language and applies the regional defaults for that locale.
     * Unknown locales are still stored (for forward-compat) but no defaults change.
     *
     * Persists to user_profiles.language (fire-and-forget) — sans ça, les emails
     * transactionnels (code de connexion, drip, invitation, bilan du dimanche)
     * continuent de lire la langue figée à la création du compte, même après un
     * changement de langue dans les réglages.
     *
     * Branché sur setLocale() (voir en bas de fichier) : plus personne n'appelle
     * cette méthode à la main pour suivre un changement de langue.
     */
    public setLanguage(language: string): void {
        // Les défauts régionaux suivent un CHANGEMENT de langue, pas une simple
        // ré-application de la même : setLocale() est appelé au montage du login
        // comme au retour d'un deep link, et ne doit pas écraser à ces occasions
        // le début de semaine que le parent a choisi lui-même.
        const changed = this.preferences.language !== language;
        this.preferences.language = language;
        const defaults = LOCALE_DEFAULTS[language as Locale];
        if (changed && defaults) {
            this.preferences.weekStartDay = defaults.weekStartDay;
            this.preferences.timeFormat = defaults.timeFormat;
        }
        this.saveToStorage();
        this.notifyListeners();
        if (this.persistedLanguage === language) return;
        this.persistedLanguage = language;
        void this.saveToDatabase();
    }
}

export const localeService = LocaleService.getInstance();

// Sens d'écriture unique : langue affichée → préférences → user_profiles.language.
// L'abonnement est posé à l'import du module, que App.vue charge au boot, donc il
// est en place avant le premier changement de langue possible.
onLocaleChange((locale) => { localeService.setLanguage(locale); });
